import { useCallback, useMemo } from 'react';

import { InsertChartOutlined } from '@mui/icons-material';
import { ActionItemKeys, NotificationResponse, PatientFilledForm } from '@uplift/api';
import { providerPatientRoute } from '@uplift/constants';
import { trackDismissToDo, useAllActionItems, useL10n } from '@uplift/hooks';
import { UserRole } from '@uplift/types';
import { LinkDashboardActionItem } from '@uplift/ui-v2';

type NormalRiskProps = {
  date: string;
  formNameMap: Record<string, string>;
  patient: PatientFilledForm;
  recommendConsult: boolean;
  time: string;
};

type SortedAssessment = {
  formId: string;
  dateSubmitted: string;
  score: number;
  shouldNotify: boolean;
  submissionId: string;
};

export const NormalRiskActionItem: React.VFC<NormalRiskProps> = ({ date, formNameMap, patient, recommendConsult, time }) => {
  const l10n = useL10n();
  const { dismiss } = useAllActionItems();

  const getFormDisplay = useCallback(
    (forms: PatientFilledForm['assessments']): string =>
      Object.keys(forms)
        .reduce((acc, cur) => [ ...acc, `<b>${formNameMap[cur]}</b>` ], [])
        .join(' and '),
    [ formNameMap ]
  );

  const forms = useMemo(
    () => getFormDisplay(patient.assessments),
    [ getFormDisplay, patient.assessments ]
  );

  const handleDismiss = useCallback(
    () => {
      dismiss({ key: ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT, id: patient.patientId });
      trackDismissToDo({
        toDoType: ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT,
        userType: UserRole.provider,
        reason: NotificationResponse.dismiss
      });
    }, [ dismiss, patient.patientId ]
  );

  const link = useMemo(() => {
    const withDates: SortedAssessment[] = Object.keys(patient.assessments).reduce((acc, cur) => {
      const item = patient.assessments[cur];

      if (item.dateSubmitted) {
        return [
          ...acc,
          {
            ...item,
            formId: cur,
          }
        ];
      }

      return acc;
    }, []);

    if (!withDates.length) {
      const first = Object.keys(patient.assessments)[0];

      return providerPatientRoute.createLink(
        {
          patientId: patient.patientId,
        },
        {
          activityType: 'assessment',
          activityId: first
        }
      );
    }

    const sorted = withDates.sort((a, b) => a.dateSubmitted.localeCompare(b.dateSubmitted));

    return providerPatientRoute.createLink(
      {
        patientId: patient.patientId
      },
      {
        activityType: 'assessment',
        activityId: sorted[0].submissionId
      }
    );
  }, [ patient.assessments, patient.patientId ]);

  return (
    <LinkDashboardActionItem
      color="primary"
      icon={<InsertChartOutlined />}
      title={l10n.dashboard.actionItems.patientFilledAssessment.title(patient.displayName.defaultDisplay, forms)}
      details={l10n.dashboard.actionItems.patientFilledAssessment.subtitle(date, time)}
      body={recommendConsult ? l10n.dashboard.actionItems.patientFilledAssessment.noReferRecommendConsultation(patient.displayName.first) : undefined}
      link={link}
      onClick={handleDismiss}
      actionText={l10n.dashboard.actionItems.patientFilledAssessment.actionText}
      dismiss={{
        text: l10n.dashboard.actionItems.patientFilledAssessment.dismissText,
        handleClick: handleDismiss
      }}
    />
  );
};
